import { Component, input, model, output, viewChild } from '@angular/core';
import { InputLabel } from '../input-label/input-label';
import { InputSimple } from '../input-simple/input-simple';
import { PopoverComponent } from '../popover/popover';
import { PopoverDirective } from '../../directives/popover-directive';
import { Button } from '../button/button';
import { NgIcon } from '@ng-icons/core';
import { form, FormField, FormValueControl } from '@angular/forms/signals';
import { CalendarRange, fromDateString, toDateString } from '../calendar-range-input/definitions';
import { CalendarRangeInput } from '../calendar-range-input/calendar-range-input';
import { DatePipe } from '@angular/common';
import { parse } from 'date-fns';

@Component({
  selector: 'ui-date-range-input',
  imports: [
    InputLabel,
    InputSimple,
    NgIcon,
    PopoverComponent,
    CalendarRangeInput,
    PopoverDirective,
    FormField,
    DatePipe,
    Button,
  ],
  templateUrl: './date-range-input.html',
})
export class DateRangeInput implements FormValueControl<CalendarRange> {
  readonly value = model<CalendarRange>({ start: null, end: null });
  readonly disabled = input<boolean>(false);
  readonly touched = model<boolean>(false);

  placeholderStart = input<string>('');
  placeholderEnd = input<string>('');

  fieldValueChange = output<CalendarRange>();

  private calendar = viewChild(CalendarRangeInput);

  protected field = form(this.value);

  private getDateFromValue(value: string) {
    if (value === '') {
      return null;
    }
    try {
      return parse(value, 'dd.MM.yyyy', new Date());
    } catch {
      return null;
    }
  }

  protected onTriggerShowCalendarPopover(popover: PopoverComponent) {
    this.touched.set(true);
    popover.show();
  }

  protected onChangeStart(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    const start = this.getDateFromValue(value);
    const startStr = start === null ? null : toDateString(start);
    this.value.update((model) => ({
      start: startStr,
      // if start is after end, clamp end to start
      end:
        start === null || (model.end !== null && fromDateString(model.end) >= start)
          ? model.end
          : startStr,
    }));
    this.fieldValueChange.emit(this.value());
    if (typeof startStr === 'string') {
      this.calendar()?.month.set(startStr);
    }
  }

  protected onClickClearStart() {
    this.touched.set(true);
    this.value.update((model) => ({ start: null, end: model.end }));
    this.fieldValueChange.emit(this.value());
  }

  protected onChangeEnd(event: Event) {
    this.touched.set(true);
    const value = (event.target as HTMLInputElement).value;
    const end = this.getDateFromValue(value);
    const endStr = end === null ? null : toDateString(end);
    this.value.update((model) => ({
      start:
        end === null || model.start === null || fromDateString(model.start) < end
          ? model.start
          : endStr,
      end: endStr,
    }));
    this.fieldValueChange.emit(this.value());
    if (typeof endStr === 'string') {
      this.calendar()?.month.set(endStr);
    }
  }

  protected onClickClearEnd() {
    this.touched.set(true);
    this.value.update((model) => ({ end: null, start: model.start }));
    this.fieldValueChange.emit(this.value());
  }
}
